import { action } from 'mobx'
import { Observer, observer } from 'mobx-react-lite'
import type { FilterProps } from '@/infra/type-utils'
import {
  Select,
  type SelectProps,
  type SelectItem,
} from '@/components/inputs/Select'
import {
  type ContainerProps,
  FormFieldContainer,
} from '@/components/FormContainer'

interface SelectContainerControlProps<V> extends Omit<
  SelectProps,
  'value' | 'onChange' | 'items'
> {
  items: SelectItem<V>[]
}

/**
 * Form field container for a {@link Select}.
 */
export const SelectContainer = observer(function SelectContainer<
  M extends object,
  K extends keyof FilterProps<M, V>,
  V,
>({
  name,
  model,
  label,
  sublabel,
  help,
  field,
  error,
  items,
  ...rest
}: ContainerProps<M, V, K, SelectContainerControlProps<V>>) {
  return (
    <FormFieldContainer
      name={name}
      model={model}
      label={label}
      sublabel={sublabel}
      help={help}
      field={field}
      error={error}
      render={(controlProps) => (
        // Using `Observer` here makes it so only the `Select`
        // re-renders when the value changes.
        <Observer>
          {() => (
            <Select
              value={model[field] as string}
              onChange={action(`change ${field}`, (e) => {
                model[field] = items[parseInt(e.target.value, 10)][0] as M[K]
              })}
              name={controlProps.name}
              items={items}
              {...rest}
            />
          )}
        </Observer>
      )}
    />
  )
})
